// InstallIconSheet — Oct 2026 (Randy pt-launch-target).
//
// Shown whenever a URL carries `?it=<target>`, which only the "Install this
// icon" button in Silo Settings produces. By the time this renders, the
// parse-time script in index.html has ALREADY linked the manifest for that
// target — so this sheet's only job is to tell the creator which two taps
// finish the install, while they are standing on the exact page the icon
// will open.
//
// Mounted once in App (inside the Router) so it covers all four destinations
// without each page having to know about installs.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DeviceMobile, X } from "@phosphor-icons/react";
import { detectMobilePlatform } from "@/components/A2HSPopup";

const LABELS = {
  public_qa: "Public Q&A",
  assistant: "AI Assistant",
  chats: "Chats",
  ytre: "YTRE",
};

export default function InstallIconSheet() {
  const [target, setTarget] = useState(null);
  const platform = detectMobilePlatform();

  useEffect(() => {
    const read = () => {
      try {
        const it = new URLSearchParams(window.location.search).get("it");
        setTarget(it && LABELS[it] ? it : null);
      } catch { setTarget(null); }
    };
    read();
    window.addEventListener("popstate", read);
    return () => window.removeEventListener("popstate", read);
  }, []);

  const dismiss = () => {
    setTarget(null);
    try {
      const params = new URLSearchParams(window.location.search);
      params.delete("it");
      const qs = params.toString();
      window.history.replaceState(
        {}, "",
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
      );
    } catch { /* ignore */ }
  };

  if (!target) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      data-testid="install-icon-sheet"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="modal-safe bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative mb-4 sm:mb-0"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]"
          data-testid="install-icon-close"
        >
          <X size={16} />
        </button>
        <div className="w-12 h-12 rounded-full bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center mx-auto">
          <DeviceMobile size={22} weight="duotone" />
        </div>
        <h2 className="font-serif-display text-xl text-[#1C1B1A] mt-3 text-center">
          Add this icon to your home screen
        </h2>
        <p className="text-sm text-[#6B6A68] mt-2 text-center leading-relaxed">
          You're on the page the icon will open. Finish the install from here and it
          will always launch straight into <strong>{LABELS[target]}</strong>.
        </p>

        {platform === "ios" && (
          <ol className="text-sm text-[#1C1B1A] mt-5 space-y-2.5 bg-[#FDFCF8] border hairline rounded-md p-4" data-testid="install-icon-steps-ios">
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">1.</span> Tap the <strong>Share icon</strong> at the bottom of Safari.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">2.</span> Scroll down → tap <strong>"Add to Home Screen"</strong>.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">3.</span> Tap <strong>Add</strong> in the top-right corner.</li>
          </ol>
        )}
        {platform === "android" && (
          <ol className="text-sm text-[#1C1B1A] mt-5 space-y-2.5 bg-[#FDFCF8] border hairline rounded-md p-4" data-testid="install-icon-steps-android">
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">1.</span> Tap the <strong>three-dot menu</strong> in Chrome.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">2.</span> Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">3.</span> Confirm with <strong>Install</strong> / <strong>Add</strong>.</li>
          </ol>
        )}
        {platform === "desktop" && (
          <p className="text-sm text-[#1C1B1A] mt-5 bg-[#FDFCF8] border hairline rounded-md p-4" data-testid="install-icon-steps-desktop">
            Home-screen icons are a phone thing. Open this same page on your phone
            and finish the install there — or use your browser's own
            <strong> Install</strong> option to pin it on this computer.
          </p>
        )}

        <p className="text-[12px] text-[#6B6A68] mt-3 text-center italic leading-relaxed">
          This icon keeps opening {LABELS[target]} even if you change the setting later.
        </p>

        <button
          onClick={dismiss}
          data-testid="install-icon-done"
          className="w-full text-sm bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] py-2.5 rounded-md mt-5"
        >
          Got it
        </button>
      </motion.div>
    </div>
  );
}
